// Offline fallback for the site access gate (see siteAccess.js/App.vue) -
// Matt: play local hotseat/solo-vs-AI on an iPad with no connection (e.g. a
// flight) after having visited the site once while online. The live
// Firebase subscription (watchSiteUserStatus) that normally gates access
// has no offline fallback of its own and can sit unresolved forever with no
// network - this caches the last-known-good CONFIRMED-APPROVED status per
// uid so store/index.js's initAuth can unlock the app from that instead of
// leaving the user stuck on the spinner.
//
// Deliberately keyed by uid (not one flag) - so the cache is only ever
// trusted for the EXACT signed-in user it was recorded for, not "someone
// was approved once on this device." A map (not a single slot) so more
// than one account on the same device (Matt + Brian sharing a device for
// testing) each get their own entry rather than clobbering each other's.
//
// Only ever stores a CONFIRMED 'approved' snapshot - see
// updateCachedApproval below. There's no user-hostile outcome from failing
// safe toward "still shows the waiting/sign-in screen" for pending/denied,
// so those are never cached, only invalidated.
const STORAGE_KEY = 'thunderstoner:cachedApproval:v1';

// Same "never worth breaking a real feature over" reasoning as
// localGameStorage.js/lastSignIn.js - localStorage can throw (Safari
// private browsing, quota exceeded, disabled), so every call here is
// best-effort.
function safeLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readAll() {
  const storage = safeLocalStorage();
  if (!storage) return {};
  try {
    return JSON.parse(storage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeAll(map) {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // best-effort only - see comment above
  }
}

// Call every time the live subscription resolves for the signed-in user
// (approved OR not) - see store/index.js's initAuth. A confirmed
// 'approved' record gets cached; anything else (pending, denied, the
// record not existing) INVALIDATES any previously cached approval for this
// exact uid, so a later revocation is never masked by a stale offline
// fallback once the live subscription has had a chance to report it.
export function updateCachedApproval(uid, record) {
  if (!uid) return;
  const map = readAll();
  if (record?.status === 'approved') {
    map[uid] = { record, cachedAt: Date.now() };
  } else {
    delete map[uid];
  }
  writeAll(map);
}

// Returns the cached record only if it's both present AND still shaped as
// an approved snapshot for this exact uid - never a generic "was approved
// at some point" flag.
export function readCachedApproval(uid) {
  if (!uid) return null;
  const entry = readAll()[uid];
  return entry && entry.record && entry.record.status === 'approved' ? entry.record : null;
}
